import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { tipFormatter, AreaGradient } from './ChartKit';

/**
 * Pestaña Diagnósticos de Reportes: cobertura sobre OTs, embudo de conversión,
 * $ generado por la cadena diagnóstico → presupuesto → remito, producción por
 * operario y ranking de hallazgos del checklist.
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

const pctOf = (parte: number, total: number) => (total > 0 ? Math.round((parte / total) * 100) : 0);

// Semáforo: verde/amarillo/rojo según el % (para el embudo y por operario)
const Dot = ({ pct }: { pct: number | null }) => {
  if (pct == null) return <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/30 shrink-0" />;
  const color = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`} />;
};

function DeltaChip({ value, suffix = '%' }: { value: number | null; suffix?: string }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const color = value > 0 ? 'text-green-600 bg-green-500/10' : value < 0 ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground bg-muted';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}{suffix} vs per. ant.</span>;
}

export default function DiagnosticosSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [serieMetric, setSerieMetric] = useState<'hechos' | 'cobertura' | 'generado' | 'conversion'>('hechos');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/diagnosticos', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando datos'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando diagnósticos...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const e = d.embudo;
  const coberturaPct = pctOf(d.cobertura.conChecklist, d.cobertura.ots);
  const porChecklist = e.hechos > 0 ? d.generado / e.hechos : 0;

  const pasos = [
    { label: 'Hechos', value: e.hechos, base: null },
    { label: 'Enviados', value: e.enviados, base: e.hechos },
    { label: 'Abiertos por el cliente', value: e.abiertos, base: e.enviados },
    { label: 'Presupuestados', value: e.presupuestados, base: e.hechos },
    { label: 'Aprobados', value: e.aprobados, base: e.presupuestados },
    // Sin presupuesto: el trabajo se hizo directo y el remito quedó enlazado
    ...((e.directos || 0) > 0 ? [{ label: 'Directo a trabajo (sin presup.)', value: e.directos, base: e.hechos }] : [])
  ];

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Cobertura del taller</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{coberturaPct}%</p>
          <div className="mb-1"><DeltaChip value={d.deltas?.coberturaPts ?? null} suffix=" pts" /></div>
          <span className="text-xs text-muted-foreground">{d.cobertura.conChecklist} de {d.cobertura.ots} OTs con checklist</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">$ Generado</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.generado)}</p>
          <div className="mb-1"><DeltaChip value={d.deltas?.generadoPct ?? null} /></div>
          <span className="text-xs text-muted-foreground">{d.generadoRemitos} remito{d.generadoRemitos === 1 ? '' : 's'} emitido{d.generadoRemitos === 1 ? '' : 's'} desde diagnósticos</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">$ por checklist hecho</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(porChecklist)}</p>
          <div className="mb-1"><DeltaChip value={d.deltas?.porChecklistPct ?? null} /></div>
          <span className="text-xs text-muted-foreground">lo que vale en promedio hacer uno</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Con hallazgos</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{pctOf(d.conHallazgo, e.hechos)}%</p>
          <div className="mb-1"><DeltaChip value={d.deltas?.conHallazgoPts ?? null} suffix=" pts" /></div>
          <span className="text-xs text-muted-foreground">{d.conHallazgo} checklist{d.conHallazgo === 1 ? '' : 's'} con al menos un punto amarillo/rojo</span>
        </CardContent></Card>
      </div>

      {/* La plata completa + operativa */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">$ En juego</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.plata?.enJuego || 0)}</p>
          <span className="text-xs text-muted-foreground">{d.plata?.enJuegoN || 0} presupuesto{(d.plata?.enJuegoN || 0) === 1 ? '' : 's'} esperando respuesta — a perseguir</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">$ Perdido</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.plata?.perdido || 0)}</p>
          <span className="text-xs text-muted-foreground">{d.plata?.perdidoN || 0} rechazado{(d.plata?.perdidoN || 0) === 1 ? '' : 's'}/vencido{(d.plata?.perdidoN || 0) === 1 ? '' : 's'}</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Días a presupuestar</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.diasAPresupuestar != null ? d.diasAPresupuestar : '—'}</p>
          <span className="text-xs text-muted-foreground">del checklist al presupuesto (promedio)</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Amarillos acumulados</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.amarillos?.total || 0}</p>
          <span className="text-xs text-muted-foreground">en {d.amarillos?.vehiculos || 0} vehículo{(d.amarillos?.vehiculos || 0) === 1 ? '' : 's'} sin presupuesto aprobado — venta futura (histórico)</span>
        </CardContent></Card>
      </div>

      {/* Evolución mensual (últimos 12 meses, independiente del período) */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Evolución mensual</h3>
            <div className="flex gap-2 text-xs">
              {([
                { k: 'hechos', label: 'checklists' },
                { k: 'cobertura', label: 'cobertura %' },
                { k: 'generado', label: '$' },
                { k: 'conversion', label: 'conversión %' }
              ] as const).map((m) => (
                <button
                  key={m.k}
                  onClick={() => setSerieMetric(m.k)}
                  className={`px-2 py-0.5 rounded border ${serieMetric === m.k ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <ChartContainer
            config={{
              hechos: { label: 'Checklists', color: 'hsl(var(--chart-2))' },
              cobertura: { label: 'Cobertura', color: 'hsl(var(--chart-2))' },
              generado: { label: 'Generado', color: 'hsl(var(--chart-2))' },
              conversion: { label: 'Conversión', color: 'hsl(var(--chart-2))' }
            }}
            className="h-[220px] w-full"
          >
            <AreaChart data={d.serie || []} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <defs>
                <AreaGradient id="fillDiag" color={`var(--color-${serieMetric})`} />
              </defs>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="ym" {...AXIS} />
              <YAxis
                {...AXIS}
                width={56}
                allowDecimals={false}
                tickFormatter={(v: number) => serieMetric === 'generado' ? money(v) : (serieMetric === 'cobertura' || serieMetric === 'conversion') ? `${v}%` : String(v)}
              />
              <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter(
                { hechos: 'Checklists', cobertura: 'Cobertura', generado: 'Generado', conversion: 'Conversión' },
                (v) => serieMetric === 'generado' ? money(v) : (serieMetric === 'cobertura' || serieMetric === 'conversion') ? `${v}%` : `${v} checklists`
              )} />} />
              <Area type="monotone" dataKey={serieMetric} stroke={`var(--color-${serieMetric})`} strokeWidth={2.4} fill="url(#fillDiag)" dot={{ r: 3 }} />
            </AreaChart>
          </ChartContainer>
          <div className="flex items-center justify-between gap-3 mt-2 text-xs text-muted-foreground">
            <span>
              {serieMetric === 'hechos' && 'Checklists hechos por mes.'}
              {serieMetric === 'cobertura' && 'Porcentaje de las OTs del taller que tuvieron checklist, por mes.'}
              {serieMetric === 'generado' && 'Facturación de remitos emitidos que nacieron de un diagnóstico, por mes.'}
              {serieMetric === 'conversion' && 'Porcentaje de checklists del mes que terminaron con presupuesto aprobado.'}
            </span>
            <span className="shrink-0">Últimos 12 meses</span>
          </div>
        </CardContent>
      </Card>

      {/* Embudo */}
      <Card>
        <CardContent className="pt-5">
          <h3 className="font-semibold text-sm mb-3">Embudo de conversión</h3>
          {e.hechos === 0 ? (
            <p className="text-sm text-muted-foreground">Sin diagnósticos en el período.</p>
          ) : (
            <div className="space-y-2">
              {pasos.map((p) => {
                const widthPct = pctOf(p.value, e.hechos);
                const saltoPct = p.base != null ? pctOf(p.value, p.base) : null;
                return (
                  <div key={p.label} className="flex items-center gap-3">
                    <span className="w-44 shrink-0 text-sm flex items-center gap-2">
                      {p.base != null ? <Dot pct={saltoPct} /> : <span className="inline-block w-2 h-2 shrink-0" />}
                      {p.label}
                    </span>
                    <div className="flex-1 h-6 rounded bg-muted/50 overflow-hidden">
                      <div className="h-full rounded bg-primary/70" style={{ width: `${Math.max(widthPct, p.value > 0 ? 4 : 0)}%` }} />
                    </div>
                    <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums">{p.value}</span>
                    <span className="w-14 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                      {saltoPct != null ? `${saltoPct}%` : ''}
                    </span>
                  </div>
                );
              })}
              <p className="text-xs text-muted-foreground pt-1">
                El % de cada fila es sobre el paso anterior (Presupuestados es sobre Hechos). Dónde cae el número, ahí está el escalón a trabajar.
              </p>
              <div className="flex flex-wrap gap-4 pt-0.5 text-xs text-muted-foreground">
                <span><span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle bg-emerald-500" />bien (≥70%)</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle bg-amber-500" />flojo (40–69%)</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle bg-red-500" />mal (&lt;40%)</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Por operario */}
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Por operario</h3>
            {d.porOperario.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin checklists en el período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground text-left">
                      <th className="pb-1.5 font-normal">Operario</th>
                      <th className="pb-1.5 font-normal text-right">Checklists</th>
                      <th className="pb-1.5 font-normal text-right">Hallazgos</th>
                      <th className="pb-1.5 font-normal text-right">Presup.</th>
                      <th className="pb-1.5 font-normal text-right">Aprob.</th>
                      <th className="pb-1.5 font-normal text-right">$ Aprobado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.porOperario.map((o: any) => (
                      <tr key={o.operario} className="border-t">
                        <td className="py-1.5 pr-2 max-w-[140px]">
                          <span className="flex items-center gap-2 min-w-0">
                            {/* Semáforo: % de sus checklists que terminan presupuestados */}
                            <Dot pct={o.checklists > 0 ? Math.round((o.presupuestados / o.checklists) * 100) : null} />
                            <span className="truncate">{o.operario}</span>
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 text-right font-medium tabular-nums">{o.checklists}</td>
                        <td className="py-1.5 pr-2 text-right text-muted-foreground tabular-nums">{o.hallazgos}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{o.presupuestados}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{o.aprobados}</td>
                        <td className="py-1.5 text-right font-medium tabular-nums">{money(o.aprobadoMonto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-muted-foreground pt-2">
                  El semáforo indica cuántos de sus checklists llegan a presupuesto: <span className="inline-block w-2 h-2 rounded-full align-middle bg-emerald-500" /> ≥70% · <span className="inline-block w-2 h-2 rounded-full align-middle bg-amber-500" /> 40–69% · <span className="inline-block w-2 h-2 rounded-full align-middle bg-red-500" /> &lt;40%.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ranking de hallazgos */}
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Qué se encuentra (top hallazgos)</h3>
            {d.hallazgos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin hallazgos en el período.</p>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground pb-1 border-b">
                  <span className="flex-1 min-w-0">Punto</span>
                  <span className="w-10 shrink-0 text-right">Enc.</span>
                  <span className="w-10 shrink-0 text-right">Pres.</span>
                  <span className="w-10 shrink-0 text-right">Aprob.</span>
                </div>
                {d.hallazgos.map((h: any) => (
                  <div key={h.punto} className="flex items-center gap-2 py-0.5">
                    <span className="flex-1 min-w-0 flex items-center gap-2 text-sm">
                      {/* Semáforo: % de lo encontrado que llega a presupuesto */}
                      <Dot pct={h.total > 0 ? Math.round(((h.presupuestados ?? 0) / h.total) * 100) : null} />
                      <span className="truncate">{h.punto}</span>
                    </span>
                    <span className="w-10 shrink-0 text-right text-sm font-medium tabular-nums">{h.total}</span>
                    <span className="w-10 shrink-0 text-right text-sm tabular-nums text-muted-foreground">{h.presupuestados ?? 0}</span>
                    <span className={`w-10 shrink-0 text-right text-sm font-medium tabular-nums ${(h.aprobados ?? 0) > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>{h.aprobados ?? 0}</span>
                  </div>
                ))}
                <p className="pt-1.5 text-xs text-muted-foreground">
                  Enc. = encontrados · Pres. = llegaron a presupuesto · Aprob. = el cliente aprobó. El semáforo mide cuánto de lo encontrado llega a presupuesto: <span className="inline-block w-2 h-2 rounded-full align-middle bg-emerald-500" /> ≥70% · <span className="inline-block w-2 h-2 rounded-full align-middle bg-amber-500" /> 40–69% · <span className="inline-block w-2 h-2 rounded-full align-middle bg-red-500" /> &lt;40%.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
